import React from "react";
import { Star, MapPin, Award } from "lucide-react";
import Image from "next/image";
import { z } from "zod";

const VendorSchema = z.object({
	name: z.string(),
	logo: z.string(),
	rating: z.number(),
	reviews: z.number(),
	location: z.string(),
	specializations: z.array(z.string()),
	verified: z.boolean(),
});

const VendorSliderSchema = z.object({
	header: z.string().default("Featured BIM Vendors"),
	description: z
		.string()
		.default(
			"Discover our top-rated BIM specialists ready to transform your architectural projects."
		),
	viewProfileText: z.string().default("View Profile"),
	buttonText: z.string().default("View All 200+ Vendors"),
	vendors: z.array(VendorSchema).default([
		{
			name: "BIM Solutions Europe",
			logo: "https://images.pexels.com/photos/3184291/pexels-photo-3184291.jpeg?auto=compress&cs=tinysrgb&w=200&h=100&fit=crop",
			rating: 4.9,
			reviews: 127,
			location: "London, UK",
			specializations: ["Revit", "Navisworks", "5D BIM"],
			verified: true,
		},
		{
			name: "Middle East BIM Experts",
			logo: "https://images.pexels.com/photos/3184465/pexels-photo-3184465.jpeg?auto=compress&cs=tinysrgb&w=200&h=100&fit=crop",
			rating: 4.8,
			reviews: 89,
			location: "Dubai, UAE",
			specializations: ["ArchiCAD", "Tekla", "4D BIM"],
			verified: true,
		},
		{
			name: "Nordic BIM Consulting",
			logo: "https://images.pexels.com/photos/3184317/pexels-photo-3184317.jpeg?auto=compress&cs=tinysrgb&w=200&h=100&fit=crop",
			rating: 4.9,
			reviews: 156,
			location: "Stockholm, Sweden",
			specializations: ["Revit", "Solibri", "VDC"],
			verified: true,
		},
		{
			name: "German Engineering BIM",
			logo: "https://images.pexels.com/photos/3184339/pexels-photo-3184339.jpeg?auto=compress&cs=tinysrgb&w=200&h=100&fit=crop",
			rating: 4.7,
			reviews: 203,
			location: "Munich, Germany",
			specializations: ["Revit", "Bentley", "Point Cloud"],
			verified: true,
		},
		{
			name: "Mediterranean BIM Studio",
			logo: "https://images.pexels.com/photos/3184360/pexels-photo-3184360.jpeg?auto=compress&cs=tinysrgb&w=200&h=100&fit=crop",
			rating: 4.8,
			reviews: 94,
			location: "Barcelona, Spain",
			specializations: ["Revit", "Dynamo", "Grasshopper"],
			verified: true,
		},
	]),
});

type VendorSliderProps = z.input<typeof VendorSliderSchema>;

const VendorSlider: React.FC<VendorSliderProps> = (props = {}) => {
	const data = VendorSliderSchema.parse(props);

	return (
		<section id="vendors" className="bg-white py-20">
			<div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
				<div className="mb-12 text-center">
					<h2 className="mb-4 text-3xl font-bold text-gray-900 md:text-4xl">{data.header}</h2>
					<p className="mx-auto max-w-3xl text-xl text-gray-600">{data.description}</p>
				</div>

				<div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
					{data.vendors.map((vendor, index) => (
						<div
							key={index}
							className="group rounded-xl border border-gray-200 bg-white p-6 transition-all duration-300 hover:-translate-y-1 hover:shadow-lg"
						>
							<div className="relative mb-4">
								<Image
									src={vendor.logo}
									alt={vendor.name}
									className="h-24 w-full rounded-lg object-cover"
									width={100}
									height={100}
								/>
								{vendor.verified && (
									<div className="absolute -right-2 -top-2 flex h-8 w-8 items-center justify-center rounded-full bg-green-500">
										<Award className="h-4 w-4 text-white" />
									</div>
								)}
							</div>

							<h3 className="mb-2 font-bold text-gray-900 transition-colors group-hover:text-primary-500">
								{vendor.name}
							</h3>

							<div className="mb-2 flex items-center">
								<div className="flex items-center">
									<Star className="h-4 w-4 fill-current text-yellow-400" />
									<span className="ml-1 text-sm font-semibold text-gray-900">{vendor.rating}</span>
								</div>
								<span className="ml-2 text-sm text-gray-600">({vendor.reviews} reviews)</span>
							</div>

							<div className="mb-3 flex items-center text-sm text-gray-600">
								<MapPin className="mr-1 h-4 w-4" />
								{vendor.location}
							</div>

							<div className="mb-4 flex flex-wrap gap-1">
								{vendor.specializations.map((spec, specIndex) => (
									<span
										key={specIndex}
										className="rounded-full bg-primary-50 px-2 py-1 text-xs text-primary-600"
									>
										{spec}
									</span>
								))}
							</div>

							<button className="w-full rounded-lg bg-primary-500 py-2 font-semibold text-white transition-colors hover:bg-primary-600">
								{data.viewProfileText}
							</button>
						</div>
					))}
				</div>

				<div className="mt-12 text-center">
					<button className="rounded-lg bg-gray-100 px-8 py-3 font-semibold text-gray-800 transition-colors hover:bg-gray-200">
						{data.buttonText}
					</button>
				</div>
			</div>
		</section>
	);
};

export default VendorSlider;
